import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId.js';
import { hasAtLeastOneField } from '../shared/refines';
import { WebhookStatusValidationEnum, WebhookUpdatableStatusValidationEnum } from './enum.js';
import {
  WebhookCustomHeadersValidationSchema,
  WebhookEventsValidationSchema,
  WebhookNameValidationSchema,
  WebhookUrlValidationSchema,
} from './create.js';

const webhookUpdateBodyShape = {
  name: WebhookNameValidationSchema.optional(),
  url: WebhookUrlValidationSchema.optional(),
  events: WebhookEventsValidationSchema.optional(),
  customHeaders: WebhookCustomHeadersValidationSchema.optional(),
  status: WebhookUpdatableStatusValidationEnum.optional(),
};

// Strict so a public PATCH cannot silently drop a field the caller believed it
// set — `{"secret":"rotated"}` must 400, not 200 with nothing changed.
export const WebhookUpdateBodyValidationSchema = z.object(webhookUpdateBodyShape).strict();

// Dashboard-only variant: its status picker lists every status, including
// `failed`, and re-submits the current value on save. `url` accepts any
// protocol here so legacy http endpoints stay editable — the update service
// enforces https for a CHANGED url. Built from the raw shape rather than
// .extend()-ing the public schema, which in zod 4 would inherit its strictness
// and reject the extra fields the dashboard resubmits.
export const WebhookInternalUpdateBodyValidationSchema = z.object({
  ...webhookUpdateBodyShape,
  status: WebhookStatusValidationEnum.optional(),
  url: z.url().max(2048).trim().optional().meta({
    description:
      'The URL Checkout Page POSTs event payloads to. A url that differs from the stored one must use https; an unchanged legacy `http://` url is accepted so the endpoint stays editable.',
  }),
});

// Header values are usually receiver credentials, which must not be typed into an
// assistant chat, so the MCP tool cannot set them. The API and the dashboard can.
const { customHeaders: _customHeaders, ...webhookToolUpdateShape } = webhookUpdateBodyShape;

/**
 * Exported so the MCP tool can iterate the same field list instead of
 * re-declaring it (drift-proof).
 */
export const WEBHOOK_UPDATE_FIELDS = Object.keys(webhookToolUpdateShape) as ReadonlyArray<
  keyof typeof webhookToolUpdateShape
>;

export const UpdateWebhookInputValidationSchema = z
  .object({
    webhookId: ObjectIdStringValidationSchema.meta({
      description: 'The webhook to update. Use list_webhooks to find the id.',
    }),
    ...webhookToolUpdateShape,
  })
  .strict()
  .refine(
    value => {
      const { webhookId: _webhookId, ...rest } = value;
      return hasAtLeastOneField(rest);
    },
    { message: 'At least one field to update must be provided.' },
  );
